import { useState } from 'react';
import { cn } from '@mlm/ui';
import type { ShowcaseRank } from '../../lib/api';
import { RankAchieversDialog } from './rank-achievers-dialog';
import { RankIcon } from './rank-icon';
import { rankDisplayName } from './rank-display';

/**
 * Grid of rank tiles (icon, name, achiever count). Clicking a tile opens the
 * achievers dialog. Shared by the storefront section and the member dashboard.
 */
export function RankShowcase({
  ranks,
  highlightCode,
  className,
}: {
  ranks: ShowcaseRank[];
  /** The viewer's own current rank, ringed. */
  highlightCode?: string | null;
  className?: string;
}) {
  const [open, setOpen] = useState<ShowcaseRank | null>(null);
  return (
    <>
      <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4', className)}>
        {ranks.map((r) => {
          const mine = !!highlightCode && r.code === highlightCode;
          return (
            <button
              key={r.code}
              type="button"
              onClick={() => setOpen(r)}
              className={cn(
                'flex min-w-0 flex-col items-center gap-2 rounded-xl border bg-card p-4 text-center transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                mine && 'border-primary ring-2 ring-primary/30',
              )}
            >
              <RankIcon iconUrl={r.iconUrl} index={r.index} className="h-12 w-12" />
              <span className="w-full truncate text-sm font-semibold capitalize text-foreground">
                {rankDisplayName(r)}
              </span>
              <span className="text-xs text-muted-foreground">
                {r.achieverCount} {r.achieverCount === 1 ? 'achiever' : 'achievers'}
              </span>
              {mine && (
                <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                  Your rank
                </span>
              )}
            </button>
          );
        })}
      </div>
      <RankAchieversDialog rank={open} onOpenChange={(v) => !v && setOpen(null)} />
    </>
  );
}
